import type { JobApplication } from '../../../services/applicationTrackingService';

const FOLLOWUP_DAYS = 7;
const DAY_MS = 86_400_000;

export function isWithinLastDay(iso?: string | null): boolean {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return false;
  return Date.now() - t <= DAY_MS;
}

export function countFollowUpsDue(apps: JobApplication[]): number {
  return apps.filter((app) => {
    if (app.application_status !== 'applied') return false;
    if (app.response_received) return false;
    const appliedAt = app.applied_date ? new Date(app.applied_date).getTime() : Date.now();
    if (!Number.isFinite(appliedAt)) return false;
    return (Date.now() - appliedAt) / DAY_MS >= FOLLOWUP_DAYS;
  }).length;
}

export function countInterviews(
  apps: JobApplication[],
  byStatus: Record<string, number>,
): number {
  const fromApps = apps.filter((a) => a.application_status === 'interviewing').length;
  const fromStats =
    (byStatus.interview_scheduled ?? 0) +
    (byStatus.interview_completed ?? 0) +
    (byStatus.interviewing ?? 0);
  return Math.max(fromApps, fromStats);
}

export function countNewMatchesToday(items: { generatedAt?: string; lastSeenAt?: string }[]): number {
  return items.filter((j) => isWithinLastDay(j.generatedAt) || isWithinLastDay(j.lastSeenAt)).length;
}

export function countApplicationsToday(apps: JobApplication[]): number {
  return apps.filter(
    (app) =>
      (app.application_status === 'applied' || app.application_status === 'in_progress') &&
      isWithinLastDay(app.applied_date),
  ).length;
}

export function daysSinceDate(iso?: string | null): number {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return 0;
  return Math.max(0, Math.floor((Date.now() - t) / DAY_MS));
}

/** Oldest applied role with no response — best candidate for a follow-up action. */
export function pickFollowUpCandidate(apps: JobApplication[]): JobApplication | null {
  const candidates = apps
    .filter((app) => {
      if (app.application_status !== 'applied') return false;
      if (app.response_received) return false;
      const appliedAt = app.applied_date ? new Date(app.applied_date).getTime() : NaN;
      if (!Number.isFinite(appliedAt)) return false;
      return (Date.now() - appliedAt) / DAY_MS >= FOLLOWUP_DAYS;
    })
    .sort(
      (a, b) =>
        new Date(a.applied_date).getTime() - new Date(b.applied_date).getTime(),
    );

  return candidates[0] ?? null;
}
